import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Header } from "@/components/Header";
import { PriceChart } from "@/components/PriceChart";
import { BuyCard } from "@/components/BuyCard";
import { SellCard } from "@/components/SellCard";
import { TxTable } from "@/components/TxTable";
import { WithdrawDialog } from "@/components/WithdrawDialog";
import { PriceAlerts } from "@/components/PriceAlerts";
import { AutoBuy } from "@/components/AutoBuy";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { useAuth } from "@/context/AuthContext";
import { usePrice } from "@/hooks/usePrice";
import { api, fmtBtc, fmtUsd } from "@/lib/api";

const Stat = ({ label, value, testid, accent }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">{label}</p>
    <p className={`mt-2 font-mono text-xl font-semibold md:text-2xl ${accent || ""}`} data-testid={testid}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const p = usePrice();
  const [txs, setTxs] = useState([]);
  const price = p?.price;

  const loadTx = useCallback(() => api.get("/transactions").then((r) => setTxs(r.data)).catch(() => {}), []);
  useEffect(() => { loadTx(); }, [loadTx]);

  const total = (user?.usd_balance || 0) + (user?.btc_balance || 0) * (price || 0);

  return (
    <div className="grain min-h-screen bg-[#08090E]">
      <Header />
      <main className="relative z-10 mx-auto max-w-7xl space-y-6 px-4 pb-16 pt-24 md:px-8 md:pt-28">
        <div className="fade-up">
          <p className="text-sm text-[#94A3B8]">Welcome back,</p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl" data-testid="dashboard-greeting">{user?.name?.split(" ")[0]}</h1>
        </div>
        <section className="card fade-up relative overflow-hidden p-6 md:p-8" style={{ animationDelay: ".05s" }}>
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#F7931A]/10 blur-3xl" />
          <div className="relative grid gap-6 sm:grid-cols-3">
            <Stat label="Total portfolio" value={fmtUsd(total)} testid="total-portfolio-value" />
            <Stat label="BTC holdings" value={`${fmtBtc(user?.btc_balance)} BTC`} testid="btc-balance-display" accent="text-[#F7931A]" />
            <div>
              <Stat label="USD balance" value={fmtUsd(user?.usd_balance)} testid="usd-balance-display" accent="text-emerald-400" />
              <WithdrawDialog onDone={loadTx} />
            </div>
          </div>
        </section>
        <div className="grid gap-4 md:gap-6 lg:grid-cols-12">
          <section className="card fade-up p-5 md:p-6 lg:col-span-7" style={{ animationDelay: ".1s" }}><PriceChart /></section>
          <section className="card fade-up p-5 md:p-6 lg:col-span-5" style={{ animationDelay: ".15s" }}>
            <Tabs defaultValue="buy">
              <TabsList className="mb-5 grid h-auto w-full grid-cols-2 rounded-xl border border-[#212638] bg-[#0F111A] p-1">
                <TabsTrigger value="buy" data-testid="buy-btc-tab" className="rounded-lg py-2.5 font-semibold data-[state=active]:bg-emerald-500 data-[state=active]:text-[#08090E]">Buy BTC</TabsTrigger>
                <TabsTrigger value="sell" data-testid="sell-btc-tab" className="rounded-lg py-2.5 font-semibold data-[state=active]:bg-red-500 data-[state=active]:text-white">Sell BTC</TabsTrigger>
              </TabsList>
              <TabsContent value="buy"><BuyCard price={price} onDone={loadTx} /></TabsContent>
              <TabsContent value="sell"><SellCard price={price} onDone={() => { loadTx(); refresh(); }} /></TabsContent>
            </Tabs>
          </section>
        </div>
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
          <section className="card fade-up p-5 md:p-6" style={{ animationDelay: ".18s" }}><PriceAlerts price={price} /></section>
          <section className="card fade-up p-5 md:p-6" style={{ animationDelay: ".2s" }}><AutoBuy onDone={loadTx} /></section>
        </div>
        <section className="card fade-up p-5 md:p-6" style={{ animationDelay: ".2s" }}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold tracking-tight">Transaction history</h3>
            <ExportCsvButton testid="dashboard-export-csv-button" />
          </div>
          <TxTable items={txs} />
        </section>
      </main>
    </div>
  );
}
